import React, { useEffect, useCallback, useState } from 'react';
import styled from 'styled-components';
import { SessionStepProps, SessionStaticProps } from './SessionFlowBase';
import { makeStyles } from '@material-ui/core';
import SessionTimeline from './Timeline/SessionTimeline';
import VideoLayout, { PanMethod } from '../VideoPlayer/VideoLayout';
import VideoControls from '../VideoPlayer/VideoControls';
import SessionEventList from './SessionEventList';
import { TSession } from './Sessions';
import useSession, { refreshSession } from '../Util/useSession';
import sessionsService from '../../services/sessions';
import { SessionEvent, secondsToTimeSpan, timeSpanToSeconds } from '../../services/sessionModels';
import { EventPlayerType, EventReason, EventType } from '../../services/sessionsPrepare';
import { useSnackbar } from 'notistack';

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    textAlign: 'left',
  },
  text: {
    display: 'flex',
    marginRight: '10px',
  },
  flexContainer: {
    display: 'flex',
  },
  sidebar: {
    width: '20%',
    height: '50vh',
  },
  videoContainer: {
    width: '80%',
    height: '50vh',
    backgroundColor: '#f5f6fa',
  },
  videoControlsContainer: {
    marginTop: '12px',
    width: '20%',
    height: '12vh',
    backgroundColor: '#ccf1',
    border: '1px dashed #000',
  },
  timelineContainer: {
    marginTop: '12px',
    width: '80%',
    height: '12vh',
  },
  errorWindow: {
    position: 'fixed',
    backgroundColor: '#feec',
    border: '1px dashed #a00',
    color: '#a00',
    padding: '6px',
    zIndex: 5,
  },
  inlineText: {
    display: 'inline',
  },
  mediaControlIcon: {
    width: '28px',
    height: '28px',
  },
  mediaControlBigIcon: {
    width: '48px',
    height: '48px',
  },
}));

const eventSort = (a: SessionEvent, b: SessionEvent) => {
  let ret = timeSpanToSeconds(a.timestamp) - timeSpanToSeconds(b.timestamp);
  if (ret === 0) {
    ret = a.id - b.id;
  }
  return ret;
};

export const EventTypeToString = (type: string) => {
  switch (type) {
    case EventType.LineupChange:
      return 'Line Up Change';
    default:
      return 'Other';
  }
};
export const EventReasonToString = (reason: string | null | undefined) => {
  switch (reason) {
    case EventReason.RedCard:
      return 'Red Card';
    case EventReason.Injury:
      return 'Injury';
    case EventReason.Substitution:
      return 'Substitution';
    default:
      return 'Other';
  }
};

export const isFullID = (session: TSession | null | undefined) => {
  if (session?.isFullId === true) {
    return true;
  }
  if (session?.isFullId === false) {
    return false;
  }
  return !!(session?.lineup && session?.lineup?.length > 0);
};

interface Props extends SessionStepProps {}

const SessionEvents: React.FunctionComponent<Props> & SessionStaticProps = ({ sessionid, setNextEnabled }) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  //state
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [videoPlaying, setVideoPlaying] = useState<boolean>(false);
  const [playTime, setPlayTime] = useState<number>(0);
  const [playbackSpeed, setPlaybackSpeed] = useState<number>(1);
  const [seekVideo, setSeekVideo] = useState<boolean>(false);
  const [timeStep, setTimeStep] = useState<number>(5);
  const { session } = useSession(sessionid);
  const [fullID, setFullID] = useState(false);

  //refs
  const divRef = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!fullID) setFullID(isFullID(session));
    // eslint-disable-next-line
  }, [session]);

  const onSeek = useCallback((time: number) => {
    setSeekVideo(true);
  }, []);

  const absoluteSeek = useCallback(
    (newTime: number) => {
      setPlayTime((prev) => {
        // Clamp time to allowed values
        const clampedTime = Math.max(0, Math.min(newTime, videoDuration));
        onSeek(clampedTime);
        return clampedTime;
      });
    },
    [onSeek, videoDuration]
  );

  const relativeSeek = useCallback(
    (timeDiff: number) => {
      setPlayTime((prev) => {
        // Clamp time to allowed values
        const clampedTime = Math.max(0, Math.min(prev + timeDiff, videoDuration));
        onSeek(clampedTime);
        return clampedTime;
      });
    },
    [videoDuration, onSeek]
  );

  const onChangeVideoDuration = useCallback((duration: number) => {
    setVideoDuration(duration);
  }, []);

  useEffect(() => {
    //set next button to enabled if we have no error messages
    const amountOfEvents: number = session?.events?.length || 0;
    let amountOfUniqueEvents: number = 0;
    session?.events?.forEach((e: SessionEvent) => {
      let findSimilar: SessionEvent | undefined = undefined;
      // Find similar timestamps (in between periods dont count)
      if(!e.offscreen) {
        findSimilar = session?.events?.find((c:SessionEvent) => {
          return timeSpanToSeconds(e.timestamp) === timeSpanToSeconds(c.timestamp) && e.id !== c.id && !c.offscreen
        })
      }

      if(findSimilar === undefined) {
        amountOfUniqueEvents += 1;
      }
    });
    const allTsUnique: boolean = amountOfEvents === amountOfUniqueEvents;
    if(!allTsUnique) {
      enqueueSnackbar('All event timestamps must be unique to continue.', { variant: 'info', autoHideDuration: 5000 });
    }
    setNextEnabled(allTsUnique);
  }, [setNextEnabled, session, enqueueSnackbar]);

  const addEvent = useCallback(async () => {
    await sessionsService.addEvent(sessionid, {
      teamId: session?.homeTeam?.id || '',
      timestamp: secondsToTimeSpan(Math.round(playTime)),
      reason: EventReason.RedCard,
      type: EventType.LineupChange,
      offscreen: false,
      out: {
        type: EventPlayerType.Player,
        number: -1,
      },
    });
    refreshSession(sessionid);
  }, [sessionid, session, playTime]);

  const deleteEvent = useCallback(
    async (event: SessionEvent) => {
      await sessionsService.deleteEvent(sessionid, event.id);
      refreshSession(sessionid);
    },
    [sessionid]
  );

  const editEvent = useCallback(
    async (event: SessionEvent) => {
      if (!event.teamId) return;
      await sessionsService.updateEvent(sessionid, event.id, {
        teamId: event.teamId,
        timestamp: event.timestamp,
        type: event.type,
        offscreen: event.offscreen,
        reason: event.reason,
        in: event.in ? { number: event.in.number, type: event.in.type } : null,
        out: event.out ? { number: event.out.number, type: event.out.type } : null,
      });
      refreshSession(sessionid);
    },
    [sessionid]
  );

  return (
    <CenterContainer>
      <Container>
        <Wrapper>
          <div className={classes.flexContainer}>
            <div className={classes.sidebar}>
              <SessionEventList
                events={session?.events?.slice()?.sort(eventSort) || []}
                lineup={session?.lineup || []}
                homeTeam={session?.homeTeam}
                awayTeam={session?.awayTeam}
                addEvent={addEvent}
                editEvent={editEvent}
                deleteEvent={deleteEvent}
                playTime={playTime}
                seekToPlayTime={absoluteSeek}
                fullID={fullID}
                periods={session?.periods || []}
              />
            </div>
            <div ref={divRef} className={classes.videoContainer}>
              <VideoLayout
                videos={session?.videos || []}
                showOverlayUI={false}
                updateIntervalMs={40}
                videoPlaying={videoPlaying}
                onChangeVideoPlaying={setVideoPlaying}
                playTime={playTime}
                playTimeChanged={setPlayTime}
                seekVideo={seekVideo}
                setSeekVideo={setSeekVideo}
                playbackSpeed={playbackSpeed}
                onChangeVideoDuration={onChangeVideoDuration}
                noFullscreen
                overridePanMethod={PanMethod.Drag}
              />
            </div>
          </div>
          <div className={classes.flexContainer}>
            <div className={classes.videoControlsContainer}>
              <VideoControls
                isPlaying={videoPlaying}
                onChangeVideoPlaying={setVideoPlaying}
                relativeSeek={relativeSeek}
                playbackSpeed={playbackSpeed}
                setPlaybackSpeed={setPlaybackSpeed}
                timeStep={timeStep}
                setTimeStep={setTimeStep}
              />
            </div>
            <div className={classes.timelineContainer}>
              <SessionTimeline
                playTime={playTime}
                seekToPlayTime={absoluteSeek}
                videoDuration={videoDuration}
                teamHome={session?.homeTeam}
                teamAway={session?.awayTeam}
                periods={session?.periods || []}
                events={session?.events || []}
              />
            </div>
          </div>
        </Wrapper>
      </Container>
    </CenterContainer>
  );
};

const CenterContainer = styled.div`
  display: table-cell;
  height: calc(100vh - 350px);
  text-align: left;
  vertical-align: middle;
  width: 100%;
`;

const Container = styled.div`
  margin: auto;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin: 10px;
`;

SessionEvents.isComplete = (session: TSession) => {
  return true;
};

export default SessionEvents;
